/** Ordered results with bounded work; failures drain in-flight jobs before rejecting. */
export async function mapConcurrent<T, R>(
  items: readonly T[],
  concurrency: number,
  work: (item: T, index: number) => Promise<R>,
): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let next = 0;
  let failed = false;
  let failure: unknown;
  await Promise.all(
    Array.from(
      { length: Math.min(items.length, Math.max(1, Math.floor(concurrency))) },
      async () => {
        while (!failed && next < items.length) {
          const index = next++;
          try {
            results[index] = await work(items[index], index);
          } catch (error) {
            failed = true;
            failure = error;
          }
        }
      },
    ),
  );
  if (failed) throw failure;
  return results;
}

/**
 * Runs at most `concurrency` tasks at once; later calls wait in FIFO order.
 * A finishing task hands its slot straight to the next waiter, so a caller
 * arriving in between can never push the count over the limit.
 */
export function createConcurrencyLimit(concurrency: number) {
  const limit = Math.max(1, Math.floor(concurrency));
  let active = 0;
  const waiting: Array<() => void> = [];
  return async function run<R>(task: () => Promise<R>): Promise<R> {
    if (active < limit) active += 1;
    else await new Promise<void>((resolve) => waiting.push(resolve));
    try {
      return await task();
    } finally {
      const next = waiting.shift();
      if (next) next();
      else active -= 1;
    }
  };
}
